/* Tea To-Go cart rules (docs/CART_LOGIC.md). Pure: reads a Shopify cart and
 * returns the single next request needed. Never prices anything itself. */
(function (root) {
  'use strict';
  const MAX_PACKS = 2;
  const GIFT_BOXES = 4;
  // The free pack at 4+ boxes is Rebuy's progress-bar gift (item-count tier, packs
  // excluded from the count; works without Shopify Plus). Rebuy adds, discounts and
  // removes it and remembers a shopper's removal; these rules only recognise it and
  // keep its slot free, so they never take it out to make room.
  const GIFT = 'Rebuy Tiered Progress Bar';

  function months(plan) {
    const values = (plan?.options || []).filter(option => option.name === 'Order Frequency and Unit').map(option => option.value);
    const match = values.length === 1 && /^([1-3])-month$/.exec(values[0] || '');
    return match ? Number(match[1]) : null;
  }
  function percent(plan) {
    const values = (plan?.price_adjustments || []).filter(value => value.value_type === 'percentage');
    return values.length === 1 && Number.isFinite(values[0].value) ? values[0].value : null;
  }
  // Tea discount by box count; 4 or more keep the 3-box tier (Kirke, Oct 6).
  function tierPercent(boxes) { return boxes >= 3 ? 20 : boxes === 2 ? 15 : boxes === 1 ? 10 : null; }
  // What fits in the shipping box: packs only ship with tea (Natazia, Oct 7), the
  // 1-box shipper fits 1, every larger one fits 2 (sheet 3.01-3.04).
  function capacity(boxes) { return boxes <= 0 ? 0 : boxes === 1 ? 1 : MAX_PACKS; }

  function state(cart, config) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Cart unavailable.');
    const subscriptions = new Map();
    let boxes = 0;
    const packs = [];
    cart.items.forEach(item => {
      const plan = item.selling_plan_allocation?.selling_plan || null;
      if (item.variant_id === config.teaVariantId) {
        boxes += item.quantity;
        if (plan) {
          const cadence = months(plan);
          if (!cadence) throw new Error('A tea subscription plan could not be verified.');
          subscriptions.set(cadence, (subscriptions.get(cadence) || 0) + item.quantity);
        }
      } else if (item.variant_id === config.packVariantId) {
        const pack = { key: item.key, quantity: item.quantity, planId: plan?.id ?? null, price: item.final_line_price,
          identity: JSON.stringify([plan?.id ?? null, Object.entries(item.properties || {}).sort()]) };
        if (plan) Object.assign(pack, { kind: 'recurring', months: months(plan), percent: percent(plan) });
        else pack.kind = item.properties?._attribution === GIFT ? 'gift' : 'once';
        packs.push(pack);
      }
    });
    const count = kind => packs.filter(pack => pack.kind === kind).reduce((sum, pack) => sum + pack.quantity, 0);
    const subscribed = [...subscriptions.values()].reduce((sum, value) => sum + value, 0);
    const eligible = boxes >= GIFT_BOXES;
    const s = {
      boxes, subscribed, subscriptions, packs,
      once: count('once'), recurring: count('recurring'), gift: count('gift'),
      eligible,
      coded: (cart.discount_codes || []).some(code => code.applicable),
      // Rebuy records a shopper's removal of its gift here (verified on the test store).
      declined: Boolean(cart.attributes?._r_declined_gifts)
    };
    s.paid = s.once + s.recurring;
    // At 4+ boxes one slot belongs to the free pack, whether or not Rebuy has added it
    // yet: "Max 2 five-pack, one paid, one free" (sheet 3.04). Recurring packs also
    // have to fit the subscription's own renewal box, which only holds subscribed tea.
    s.capacity = capacity(boxes);
    s.paidLimit = eligible ? s.capacity - 1 : s.capacity;
    s.recurringLimit = Math.min(s.paidLimit, capacity(subscribed));
    return s;
  }

  // Pack plans come from the pack's /products/<handle>.js data.
  function packPlans(product) {
    return (product?.selling_plan_groups || []).flatMap(group => group.selling_plans || [])
      .map(plan => ({ id: plan.id, months: months(plan), percent: percent(plan) }))
      .filter(plan => Number.isSafeInteger(plan.id) && plan.months && plan.percent);
  }
  function planFor(plans, cadence, boxes) {
    const target = tierPercent(boxes);
    const matches = plans.filter(plan => plan.months === cadence && plan.percent === target);
    return matches.length === 1 ? matches[0].id : null;
  }

  // Lines are addressed by their exact key, plan changes included (Shopify accepts it;
  // verified Oct 10): paid and free packs share a variant, and line numbers shift whenever
  // Rebuy adds or removes its gift. `targets` names the lines a change is about.
  const change = (pack, body) => ({ endpoint: 'cart/change.js', body: { id: pack.key, ...body }, targets: [pack.key] });
  const reduce = (pack, by, reason) => ({ ...change(pack, { quantity: Math.max(0, pack.quantity - by) }), reason });
  const last = (s, kind) => s.packs.filter(pack => pack.kind === kind).at(-1);

  // The cart is valid exactly when this returns null; checkout waits until it does.
  function nextChange(cart, config, plans) {
    const s = state(cart, config);
    // 1. No tea, no packs (the free one included).
    if (!s.boxes) return s.packs.length ? reduce(s.packs[0], s.packs[0].quantity, 'no_box') : null;
    // 2. One pack row: paid packs live on one line, the newest one (Shopify lists the
    // newest first), so its choice — every delivery or this order only — wins. Lines that
    // differ (a pack from the product page, a Rebuy widget's tracking properties) move
    // onto it in one request, so no pack is lost or doubled on the way. Identical lines
    // are Shopify's own split (an automatic discount on some units): merging would only
    // recreate them, so they are left as they are.
    const paid = s.packs.filter(pack => pack.kind !== 'gift');
    if (new Set(paid.map(pack => pack.identity)).size > 1) {
      const updates = Object.fromEntries(paid.map((pack, index) => [pack.key, index ? 0 : s.paid]));
      const reason = paid.some(pack => pack.kind !== paid[0].kind) ? (paid[0].kind === 'recurring' ? 'one_choice_recurring' : 'one_choice_once') : 'one_row';
      return { endpoint: 'cart/update.js', body: { updates }, targets: Object.keys(updates), reason };
    }    // 3. Recurring packs follow the tea subscription: its cadence and its tier.
    for (const pack of s.packs.filter(value => value.kind === 'recurring')) {
      let cadence = pack.months;
      if (!s.subscriptions.has(cadence)) {
        if (s.subscriptions.size !== 1) return { ...change(pack, { quantity: pack.quantity, selling_plan: null }), reason: 'parent_onetime' };
        cadence = [...s.subscriptions.keys()][0];
      }
      const plan = planFor(plans, cadence, s.subscriptions.get(cadence));
      if (!plan) throw new Error('The Tea To-Go subscription price could not be verified.');
      if (plan !== pack.planId) return { ...change(pack, { quantity: pack.quantity, selling_plan: plan }), reason: 'follow_parent' };
    }
    // 4. The free pack: only at 4+ boxes, only one, never charged. Rebuy removes it below
    // 4 itself; a stale one is taken out here too, so the box never holds a third pack.
    // Codes keep the sheet's "Stacks with", so one that doesn't combine with the gift
    // and wins prices it: take it out with a notice (Rebuy remembers that).
    const gift = last(s, 'gift');
    if (gift && !s.eligible) return reduce(gift, gift.quantity, 'gift_ineligible');
    const charged = s.packs.find(pack => pack.kind === 'gift' && Number.isSafeInteger(pack.price) && pack.price > 0);
    if (charged) return reduce(charged, charged.quantity, 'gift_conflict');
    if (s.gift > 1) return reduce(gift, s.gift - 1, 'gift_quantity');
    // 5. The pack row fits what is left.
    if (s.paid > s.paidLimit) return reduce(paid[0], s.paid - s.paidLimit, s.eligible ? 'gift_slot' : 'capacity');
    // 6. Subscribed packs fit the subscribed boxes' own deliveries.
    if (s.recurring > s.recurringLimit) return reduce(last(s, 'recurring'), s.recurring - s.recurringLimit, 'subscription_capacity');
    return null;
  }

  // Why another pack cannot be added (null when it can). One wording everywhere.
  function blocked(s, kind) {
    if (!s.boxes) return 'Tea To-Go ships with your tea. Add a box of Rest + Digest first.';
    if (s.paid >= s.paidLimit) return s.eligible ? 'Max reached: 1 free pack + 1 more with ' + GIFT_BOXES + '+ boxes.'
      : s.capacity === 1 ? '1 box fits 1 Tea To-Go pack. Add a 2nd box to fit 2.' : "You've reached the max of 2 Tea To-Go packs.";
    if (kind === 'recurring' && !s.subscribed) return 'Choose a tea subscription first.';
    if (kind === 'recurring' && s.recurring >= s.recurringLimit) {
      return s.subscribed === 1 ? '1 subscribed box fits 1 pack per delivery.' : 'Your subscription is at its pack limit.';
    }
    return null;
  }

  // The Tea To-Go upsell adds; pack lines change their own quantity. Both return the
  // request(s) to send in order.
  // The pack plan for "every delivery": the cart's one tea subscription, at its tier.
  function recurringPlan(s, plans) {
    if (s.subscriptions.size !== 1) throw new Error('Choose a tea subscription first.');
    const [[cadence, boxes]] = s.subscriptions;
    const plan = planFor(plans, cadence, boxes);
    if (!plan) throw new Error('The Tea To-Go subscription price could not be verified.');
    return plan;
  }
  function add(cart, config, plans, kind) {
    const s = state(cart, config);
    const reason = blocked(s, kind);
    if (reason) throw new Error(reason);
    if (kind === 'recurring') {
      const plan = recurringPlan(s, plans);
      const line = s.packs.find(pack => pack.kind === 'recurring' && pack.planId === plan);
      return [line ? change(line, { quantity: line.quantity + 1 })
        : { endpoint: 'cart/add.js', body: { items: [{ id: config.packVariantId, quantity: 1, selling_plan: plan }] } }];
    }
    if (kind !== 'once') throw new Error('Unknown Tea To-Go option.');
    const line = s.packs.find(pack => pack.kind === 'once');
    return [line ? change(line, { quantity: line.quantity + 1 })
      : { endpoint: 'cart/add.js', body: { items: [{ id: config.packVariantId, quantity: 1 }] } }];
  }
  // The pack row's dropdown: this order only, or every delivery with the tea. A
  // subscribed choice keeps only what the subscribed boxes' deliveries fit.
  function choose(cart, config, plans, key, kind) {
    const s = state(cart, config);
    const line = s.packs.find(pack => pack.key === key && pack.kind !== 'gift');
    if (!line) throw new Error('That Tea To-Go pack is no longer in your cart.');
    if (kind === line.kind) return [];
    if (kind === 'once') return [change(line, { quantity: line.quantity, selling_plan: null })];
    if (kind !== 'recurring') throw new Error('Unknown Tea To-Go option.');
    return [change(line, { quantity: Math.min(line.quantity, s.recurringLimit), selling_plan: recurringPlan(s, plans) })];
  }
  function step(cart, config, key, delta) {
    const s = state(cart, config);
    const line = s.packs.find(pack => pack.key === key);
    if (!line) throw new Error('That Tea To-Go pack is no longer in your cart.');
    if (delta < 0) return [change(line, { quantity: line.quantity - 1 })]; // Removing the free pack: Rebuy remembers.
    if (line.kind === 'gift') throw new Error('The free pack is 1 per order.');
    const reason = blocked(s, line.kind);
    if (reason) throw new Error(reason);
    return [change(line, { quantity: line.quantity + 1 })];
  }

  const plural = (n, word) => n + ' ' + word + (n === 1 ? '' : 'es');
  // The pencil bar counts boxes only (sheet: "so the pencil bar above always makes
  // sense"; Kirke, Oct 10: no money in the cart's shipping wording). Subscription savings
  // follow the subscribed boxes, free shipping and the free pack every box in the order.
  // Checkout's own rule is $75.50 after discounts, which 2 boxes always reach (sheet).
  function reward(s) {
    const b = s.boxes, sub = s.subscribed;
    const steps = sub
      ? [['1 box · 10% off', sub >= 1], ['2 boxes · 15% off + free shipping', sub >= 2], ['3 boxes · 20% off', sub >= 3], [GIFT_BOXES + ' boxes · free Tea To-Go', s.eligible]]
      : [['1 box', b >= 1], ['2 boxes · free shipping', b >= 2], [GIFT_BOXES + ' boxes · free Tea To-Go', s.eligible]];
    let message;
    if (!b) message = 'Subscribe & save up to 20% on every order.';
    else if (s.eligible) {
      // Free every delivery only when the subscription itself has 4+ boxes (sheet:
      // "if they subscribe to 4 boxes ... 1x 5 pack is free per shipment"); otherwise
      // this order only ("free in that shipment").
      // Qualifying is not the same as having it: the shopper removed it, or a discount
      // code that doesn't combine with it won (the cart took the then-charged pack out).
      if (!s.gift && s.declined) message = 'You removed the free Tea To-Go from this order.' + (sub >= GIFT_BOXES ? ' It still comes free with every delivery.' : '');
      else if (!s.gift && s.coded) message = "The free Tea To-Go doesn't combine with some discount codes."; // True whether or not this code is one.
      else message = sub >= GIFT_BOXES ? 'Free Tea To-Go with this order and every delivery.'
        : 'Free Tea To-Go with this order.' + (sub ? ' Subscribe to all ' + GIFT_BOXES + ' boxes to get one every delivery.' : '');
    } else if (sub && sub === b) {
      message = sub === 1 ? 'Add a 2nd box for 15% off + free shipping.' : sub === 2 ? 'Add 1 more box for 20% off.'
        : 'Add ' + plural(GIFT_BOXES - sub, 'more box') + ' for a free Tea To-Go with every delivery.';
    } else {
      // Shipping counts every box (2 ship free), so a mixed cart says so even when its
      // subscription is still on the 1-box tier.
      message = b === 1 ? 'Add 1 more box to unlock free shipping.' : 'Free shipping unlocked. Add ' + plural(GIFT_BOXES - b, 'more box') + ' for a free Tea To-Go.';
      if (sub) message += sub === 1 ? ' Your subscribed box saves 10%.' : ' Your ' + sub + ' subscribed boxes save ' + tierPercent(sub) + '%.';
    }
    return { labels: steps.map(value => value[0]), reached: steps.map(value => value[1]), progress: Math.min(b / GIFT_BOXES, 1), message };
  }

  const api = Object.freeze({ months, percent, tierPercent, capacity, state, packPlans, planFor, nextChange, blocked, add, choose, step, reward, GIFT, GIFT_BOXES });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.TeaPackModel = api;
})(typeof window === 'undefined' ? globalThis : window);
